import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import API_BASE_URL from '../config';
import { useAppResume } from '../hooks/useAppResume';
import { recordNetworkFailure, recordNetworkSuccess } from '../hooks/useNetworkRecovery';
import { fetchWithRetry } from '../utils/fetchWithRetry';

interface User {
  id: number;
  email: string;
  display_name: string;
  avatar_url?: string;
  role?: 'admin' | 'user';
  location_text?: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, displayName: string) => Promise<{ success: boolean; error?: string }>;
  googleLogin: (googleToken: string) => Promise<{ success: boolean; error?: string }>;
  appleLogin: (identityToken: string, user?: { email?: string; name?: { firstName?: string; lastName?: string } }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
  updateDisplayName: (displayName: string) => Promise<{ success: boolean; error?: string }>;
  updateLocation: (locationText: string) => Promise<{ success: boolean; error?: string }>;
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to claim pending item shares after login/signup
const claimPendingSharesAsync = async (authToken: string) => {
  try {
    await fetch(`${API_BASE_URL}/api/sharing/claim-shares`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });
  } catch (error) {
    // Silently fail - not critical for login flow
    console.error('Failed to claim pending shares:', error);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token on app load
    const storedToken = localStorage.getItem('auth_token');
    const storedBackendUrl = localStorage.getItem('auth_backend_url');
    
    console.log('[Auth] App load - checking stored credentials');
    console.log(`  API_BASE_URL: ${API_BASE_URL}`);
    console.log(`  storedBackendUrl: ${storedBackendUrl}`);
    console.log(`  storedToken exists: ${!!storedToken}`);
    
    // If backend URL has changed, clear the token
    if (storedBackendUrl && storedBackendUrl !== API_BASE_URL) {
      console.log('[Auth] Backend URL changed, clearing stale token');
      console.log(`  Previous: ${storedBackendUrl}`);
      console.log(`  Current: ${API_BASE_URL}`);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_backend_url');
      setLoading(false);
      return;
    }
    
    // Safety timeout: ensure loading resolves within 20s even if fetch hangs (e.g. AbortController bug on iPad)
    const safetyTimeout = setTimeout(() => {
      console.warn('[Auth] Safety timeout: forcing loading=false after 20s');
      setLoading(false);
    }, 20000);

    if (storedToken) {
      setToken(storedToken);
      fetchCurrentUser(storedToken).finally(() => clearTimeout(safetyTimeout));
    } else {
      clearTimeout(safetyTimeout);
      setLoading(false);
    }

    return () => clearTimeout(safetyTimeout);
  }, []);

  const fetchCurrentUser = async (authToken: string, isResume = false) => {
    console.log(`[Auth] fetchCurrentUser called, isResume=${isResume}`);
    console.log(`  Calling: ${API_BASE_URL}/api/auth/me`);
    try {
      // Use shorter timeout/retries for initial load to reduce blank-screen time on poor networks.
      // Resume checks use longer values since background recovery may need more patience.
      const timeout = isResume ? 30000 : 15000;
      const retries = isResume ? 3 : 1;
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        timeout,
        retries,
        onRetry: (attempt) => {
          console.log(`[Auth] Retrying fetchCurrentUser, attempt ${attempt}`);
        },
      });

      console.log(`[Auth] /api/auth/me response status: ${response.status}`);
      if (response.ok) {
        const data = await response.json();
        console.log('[Auth] Fetched user data:', data.user);
        setUser(data.user);
        recordNetworkSuccess(); // Network is working
      } else if (response.status === 401) {
        // Only clear auth on 401 Unauthorized (invalid/expired token)
        console.error('[Auth] Token invalid (401), logging out');
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      } else {
        // Other errors (500, 503, etc.) - don't log out, just log error
        console.error('[Auth] Failed to fetch user, status:', response.status, '- keeping user logged in');
        // On initial load, throw error to trigger ErrorBoundary auto-retry
        if (!isResume) {
          throw new Error(`Server error (${response.status}): ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      recordNetworkFailure(); // Track network failure for recovery
      
      // On initial load, throw error to trigger ErrorBoundary auto-retry
      if (!isResume) {
        localStorage.removeItem('auth_token');
        setToken(null);
        throw error; // Re-throw to trigger ErrorBoundary
      }
    } finally {
      setLoading(false);
    }
  };

  // Re-validate token when app resumes from background
  // This handles the case where the token may have expired during long background idle
  useAppResume(
    useCallback(() => {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        console.log('[Auth] App resumed, re-validating token');
        fetchCurrentUser(storedToken, true);
      }
    }, []),
    30000 // Trigger if backgrounded for 30+ seconds
  );

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('[Auth] Login successful, user data:', data.user);
        console.log('[Auth] User role:', data.user?.role);
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_backend_url', API_BASE_URL);
        
        // Claim any pending item shares for this user
        claimPendingSharesAsync(data.token);
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const register = async (email: string, password: string, displayName: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password, 
          display_name: displayName 
        }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_backend_url', API_BASE_URL);
        
        // Claim any pending item shares for this new user
        claimPendingSharesAsync(data.token);
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const googleLogin = async (googleToken: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: googleToken }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('[Auth] Google login successful, user data:', data.user);
        console.log('[Auth] User role:', data.user?.role);
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_backend_url', API_BASE_URL);
        
        // Claim any pending item shares for this user
        claimPendingSharesAsync(data.token);
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Google login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const appleLogin = async (identityToken: string, user?: { email?: string; name?: { firstName?: string; lastName?: string } }): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/apple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          identityToken,
          user: user ? {
            email: user.email,
            firstName: user.name?.firstName,
            lastName: user.name?.lastName,
          } : undefined
        }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('[Auth] Apple login successful, user data:', data.user);
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_backend_url', API_BASE_URL);
        
        // Claim any pending item shares for this user
        claimPendingSharesAsync(data.token);
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Apple login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const updateDisplayName = async (displayName: string): Promise<{ success: boolean; error?: string }> => {
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ display_name: displayName }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok && data.user) {
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to update name' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const updateLocation = async (locationText: string): Promise<{ success: boolean; error?: string }> => {
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/me/location`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ location_text: locationText }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok) {
        setUser(prev => prev ? { ...prev, location_text: data.location_text, latitude: data.latitude ?? null, longitude: data.longitude ?? null } : prev);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to update location' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const deleteAccount = async (): Promise<{ success: boolean; error?: string }> => {
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/api/auth/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ confirmation: 'DELETE' }),
        timeout: 30000,
        retries: 3,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Account deleted successfully - log out immediately
        logout();
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to delete account' };
      }
    } catch (error) {
      return { success: false, error: 'Network error occurred' };
    }
  };

  const logout = () => {
    // Clear auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_backend_url');
    // Clear session-specific data
    // Note: We DON'T clear inventory-collection or inventory-last-user-id here
    // iOS apps are single-user by design - same user will likely log back in
    // If a different user logs in, useInventoryState detects this and auto-clears preferences
    localStorage.removeItem('app-activeTab');
    // Clear IAP processed transactions so they don't auto-activate for a different user
    // Note: iap_purchase_owner is intentionally preserved to prevent cross-user restore
    localStorage.removeItem('iap_processed_transactions');
    // Clear global AI consent key so a new user on this device gets the prompt
    // Per-user keys (ai-data-consent-{id}) are preserved for returning users
    localStorage.removeItem('ai-data-consent');
    // Redirect immediately to prevent welcome screen flash
    window.location.href = '/help';
    // State updates happen after redirect starts
    setUser(null);
    setToken(null);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    googleLogin,
    appleLogin,
    logout,
    loading,
    updateDisplayName,
    updateLocation,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
